import { Injectable } from '@angular/core';

import { HttpClientService } from '../common/http-client.service';

import { Involvement } from '../../models/involvement';

import { InvolvementsComponent } from '../../components/involvements/involvements/involvements.component';

@Injectable()
export class InvolvementService {
  /**
   * URL for involvements controller
   * @type {String}
   */
  private involvementsURL = 'involvements';

  /**
   * Involvements component for current matter show page for sharing between other components
   * @type {InvolvementsComponent}
   */
  involvementsComponent: InvolvementsComponent;

  /**
   * Constructor
   * @param {HttpClientService} private httpClient Service for making HTTP requests with different methods
   */
  constructor(private httpClient: HttpClientService) {}

  /**
   * Build involvements URL under matters URL
   * @param  {number} matter_id Id of matter
   * @return {string}           URL built
   */
  private buildMatterURL(matter_id: number): string {
    return `/matters/${matter_id}/${this.involvementsURL}`;
  }

  /**
   * Create outgoing envelope for contact
   * @param  {number}       involvement_id Involvement id of the contact
   * @param  {[type]}       address_id     Address for the envelope
   * @return {Promise<any>}                Server response with status of request
   */
  createOutgoingEnvelope(involvement_id: number, address_id: number): Promise<any> {
    return this.httpClient.post(`${this.involvementsURL}/${involvement_id}/create_outgoing_envelope.json`, { address_id: address_id });
  }

  /**
   * Delete involvement of contact from matter
   * @param  {number}       involvement_id Id of the involvement
   * @return {Promise<any>}                Server response with status of request
   */
  delete(involvement_id: number): Promise<any> {
    return this.httpClient.delete(`${this.involvementsURL}/${involvement_id}.json`);
  }

  /**
   * Get additional information for the involvement
   * @param  {number}       involvement_id Id of the involvement
   * @return {Promise<any>}                Server response with additional information for the involvement
   */
  getAdditionalInfo(involvement_id: number): Promise<any> {
    return this.httpClient.get(`${this.involvementsURL}/${involvement_id}/involvement_additional_info.json`, { get_additional_info: true });
  }

  /**
   * Get contact information for involvement form
   * @param  {any}          contact_id Id of contact to fetch info
   * @return {Promise<any>}            Server response with contact info
   */
  getContactInfo(contact_id: any): Promise<any> {
    return this.httpClient.get(`contacts/${contact_id}.json`, { get_info_for_involvement: true });
  }

  /**
   * Get form select options for the involvement form
   * @return {Promise<any>} Server response with contact info
   */
  getFormOptions(): Promise<any> {
    return this.httpClient.get(`${this.involvementsURL}.json`, { get_form_options: true });
  }

  /**
   * Get involvement for show
   * @param  {number}       involvement_id Id of involvement
   * @return {Promise<any>}                Server response with involvement
   */
  getInvolvement(involvement_id: number): Promise<any> {
    return this.httpClient.get(`${this.involvementsURL}/${involvement_id}.json`, { get_involvement: true });
  }

  /**
   * Get involvement for edit
   * @param  {number}       involvement_id Id of involvement
   * @return {Promise<any>}                Server response with involvement for edit
   */
  getInvolvementEdit(involvement_id: number): Promise<any> {
    return this.httpClient.get(`${this.involvementsURL}/${involvement_id}/edit.json`);
  }

  /**
   * Get involvements for matter
   * @param  {number}       matter_id Id of matter
   * @return {Promise<any>}           Server response with involvements
   */
  getInvolvements(matter_id: number): Promise<any> {
    return this.httpClient.get(`${this.buildMatterURL(matter_id)}.json`, { v6: true });
  }

  /**
   * Save involvement
   * @param  {Involvement}  involvement Involvement to save
   * @return {Promise<any>}             Server response with request status
   */
  saveInvolvement(involvement: Involvement): Promise<any> {
    if (involvement.id) return this.httpClient.put(`${this.involvementsURL}/${involvement.id}.json`, { involvement: involvement, save_involvement: true });
    else return this.httpClient.post(`${this.involvementsURL}.json`, { involvement: involvement, save_involvement: true });
  }
}
